import { useState, useRef, useCallback, useContext } from "react";
import AuthContext from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import { Phone, Clock, TrendingUp, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router";
import CreativeStudioGallery from "./CreativeStudioGallery";

const cleanFileName = (name) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9.\-_]/g, "-")
        .replace(/-+/g, "-");
};

// ── TEMPLATES ────────────────────────────────────────────────────────────────
const T = [
    { id: 1, label: "Gold", overlay: "linear-gradient(160deg,rgba(6,4,1,.50) 0%,rgba(90,62,8,.80) 100%)", accent: "#D4A843", rgb: "212,168,67", text: "#FFF9EC", font: "'Playfair Display',serif", body: "'Lato',sans-serif", hl: "38px", badge: "border", cAngle: 160, cStop1: [6, 4, 1, .50], cStop2: [90, 62, 8, .80], cFont: "Playfair Display", cBody: "Lato" },
    { id: 2, label: "Pink", overlay: "linear-gradient(148deg,rgba(120,0,50,.68) 0%,rgba(45,0,22,.90) 100%)", accent: "#F472B6", rgb: "244,114,182", text: "#fff", font: "'Abril Fatface',cursive", body: "'Nunito',sans-serif", hl: "42px", badge: "fill", cAngle: 148, cStop1: [120, 0, 50, .68], cStop2: [45, 0, 22, .90], cFont: "Abril Fatface", cBody: "Nunito" },
    { id: 3, label: "Noir", overlay: "linear-gradient(180deg,rgba(2,2,10,.38) 0%,rgba(2,2,10,.94) 54%)", accent: "#38BDF8", rgb: "56,189,248", text: "#fff", font: "'Bebas Neue',cursive", body: "'DM Sans',sans-serif", hl: "48px", badge: "pill", cAngle: 180, cStop1: [2, 2, 10, .38], cStop2: [2, 2, 10, .94], cFont: "Bebas Neue", cBody: "DM Sans" },
    { id: 4, label: "Blush", overlay: "linear-gradient(155deg,rgba(255,235,228,.60) 0%,rgba(150,70,55,.80) 100%)", accent: "#9B1C1C", rgb: "155,28,28", text: "#1C0505", font: "'Cormorant Garamond',serif", body: "'Lato',sans-serif", hl: "34px", badge: "border", cAngle: 155, cStop1: [255, 235, 228, .60], cStop2: [150, 70, 55, .80], cFont: "Cormorant Garamond", cBody: "Lato" },
    { id: 5, label: "Green", overlay: "linear-gradient(158deg,rgba(0,22,12,.46) 0%,rgba(0,42,24,.90) 100%)", accent: "#34D399", rgb: "52,211,153", text: "#ECFDF5", font: "'Cinzel',serif", body: "'Jost',sans-serif", hl: "34px", badge: "border", cAngle: 158, cStop1: [0, 22, 12, .46], cStop2: [0, 42, 24, .90], cFont: "Cinzel", cBody: "Jost" },
    { id: 6, label: "Desi", overlay: "linear-gradient(152deg,rgba(72,18,0,.58) 0%,rgba(24,6,0,.92) 100%)", accent: "#FBBF24", rgb: "251,191,36", text: "#FFFBEB", font: "'Yeseva One',cursive", body: "'Hind',sans-serif", hl: "38px", badge: "fill", cAngle: 152, cStop1: [72, 18, 0, .58], cStop2: [24, 6, 0, .92], cFont: "Yeseva One", cBody: "Hind" },
];

// ── OFFERS ───────────────────────────────────────────────────────────────────
const OFFERS = [
    { id: 1, emoji: "☀️", short: "Sunday Deal", headline: ["SUNDAY", "SPECIAL"], sub: "Haircut + Blow Dry", was: "₹798", now: "₹599", tag: "25% OFF", note: "Aaj ke liye sirf!" },
    { id: 2, emoji: "🌸", short: "Holi Offer", headline: ["PRE-HOLI", "GLOW"], sub: "Facial + Hair Spa", was: "₹1,499", now: "₹999", tag: "SAVE ₹500", note: "14 March tak" },
    { id: 3, emoji: "🎁", short: "New Member", headline: ["FIRST VISIT", "OFFER"], sub: "Koi bhi 2 Services", was: "", now: "₹799", tag: "FLAT ₹799", note: "Naye customers" },
    { id: 4, emoji: "⏰", short: "Happy Hours", headline: ["HAPPY", "HOURS"], sub: "Sab Services", was: "", now: "20% OFF", tag: "2–5 PM", note: "Weekdays only" },
    { id: 5, emoji: "💆", short: "Glow Combo", headline: ["GLOW", "COMBO"], sub: "Cleanup+Waxing+Threading", was: "₹1,200", now: "₹849", tag: "SAVE ₹351", note: "Is hafte tak" },
];

// ── MINI CREATIVE (thumbnail) ────────────────────────────────────────────────
function MiniCreative({ t, o, img, logo }) {
    return (
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", borderRadius: 6, overflow: "hidden", background: "#1a1a2e" }}>
            {img && <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
            <div style={{ position: "absolute", inset: 0, background: t.overlay }} />
            <div style={{ position: "absolute", inset: 0, padding: 5, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ width: 11, height: 11, borderRadius: "50%", border: `1px solid ${t.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 4.5, fontWeight: 800, color: t.accent, background: "#fff", overflow: "hidden" }}>
                        {logo
                            ? <img src={logo} alt="Store Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : o.brand.initials[0]
                        }
                    </div>
                    <div style={{ fontSize: 3.5, fontWeight: 700, color: t.badge === "fill" ? "#000" : t.accent, background: t.badge === "fill" ? t.accent : `rgba(${t.rgb},.18)`, padding: "1px 3px", borderRadius: t.badge === "pill" ? 8 : 2, border: t.badge === "border" ? `0.5px solid ${t.accent}` : "none" }}>{o.tag}</div>
                </div>
                <div>
                    <div style={{ fontFamily: t.font, fontSize: `${parseInt(t.hl) * 0.25}px`, fontWeight: 900, color: t.text, lineHeight: 1 }}>{o.headline[0]}</div>
                    <div style={{ fontFamily: t.font, fontSize: `${parseInt(t.hl) * 0.25}px`, fontWeight: 900, color: t.accent, lineHeight: 1 }}>{o.headline[1]}</div>
                </div>
                <div style={{ fontSize: 3, color: t.text, opacity: .6, fontFamily: t.body }}>{o.brand.name}</div>
            </div>
        </div>
    );
}

// ── FULL CREATIVE ────────────────────────────────────────────────────────────
function Creative({ t, o, img, logo }) {
    return (
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", borderRadius: 16, overflow: "hidden", background: "#111", boxShadow: "0 20px 60px rgba(0,0,0,.30)" }}>
            {img
                ? <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                : <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, background: "linear-gradient(135deg,#1e1b4b,#312e81,#1e1b4b)" }}>
                    <ImageIcon size={32} strokeWidth={1.5} style={{ opacity: 0.25 }} />
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.2)", textAlign: "center", maxWidth: 160, lineHeight: 1.5 }}>Upar se salon ki photo lagaao</div>
                </div>
            }
            <div style={{ position: "absolute", inset: 0, background: t.overlay }} />
            <div style={{ position: "absolute", inset: 0, padding: 22, display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: t.body }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ width: 46, height: 46, borderRadius: "50%", border: `2px solid ${t.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: t.font, fontSize: 14, fontWeight: 800, color: t.accent, background: "#fff", backdropFilter: "blur(8px)", flexShrink: 0, overflow: "hidden" }}>
                        {logo
                            ? <img src={logo} alt="Store Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : o.brand.initials
                        }
                    </div>
                    <div style={{ padding: t.badge === "pill" ? "4px 12px" : "4px 10px", borderRadius: t.badge === "pill" ? 20 : 5, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: t.badge === "fill" ? "#000" : t.accent, background: t.badge === "fill" ? t.accent : `rgba(${t.rgb},.15)`, border: t.badge === "border" ? `1.5px solid ${t.accent}` : "none", backdropFilter: "blur(6px)" }}>{o.tag}</div>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "12px 0" }}>
                    <div style={{ fontFamily: t.font, fontSize: t.hl, fontWeight: 900, color: t.text, lineHeight: .98, marginBottom: 8, textShadow: "0 2px 20px rgba(0,0,0,.5)" }}>
                        <div>{o.headline[0]}</div>
                        <div>{o.headline[1]}</div>
                    </div>
                    <div style={{ fontSize: 13, color: t.text, opacity: .80, marginBottom: 12, fontWeight: 400 }}>{o.sub}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {o.was && <span style={{ fontSize: 14, color: t.text, opacity: .38, textDecoration: "line-through" }}>{o.was}</span>}
                        <span style={{ fontFamily: t.font, fontSize: `calc(${t.hl} * .88)`, fontWeight: 900, color: t.accent, lineHeight: 1, textShadow: `0 0 28px rgba(${t.rgb},.5)` }}>{o.now}</span>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 10, color: t.accent, opacity: .78, fontWeight: 600, letterSpacing: .5, display: "flex", alignItems: "center", gap: 4 }}>
                        <Clock size={10} strokeWidth={2.5} />
                        {o.note}
                    </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <div style={{ fontFamily: t.font, fontSize: 13, fontWeight: 700, color: t.text }}>{o.brand.name}</div>
                        <div style={{ fontSize: 10, color: t.text, opacity: .45, marginTop: 1 }}>{o.brand.area}</div>
                    </div>
                    <div style={{ padding: "5px 10px", background: "rgba(255,255,255,.10)", backdropFilter: "blur(8px)", borderRadius: 7, border: "1px solid rgba(255,255,255,.13)", fontSize: 11, fontWeight: 700, color: t.text, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                        <Phone size={11} strokeWidth={2.5} />
                        {o.brand.phone}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function App() {
    const [tmpl, setTmpl] = useState(T[0]);
    const [offerBase, setOfferBase] = useState(OFFERS[0]);
    const [img, setImg] = useState(null);
    const [drag, setDrag] = useState(false);
    const [sent, setSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [dlState, setDLState] = useState("idle"); // idle | loading | done
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [storeGalleryImages, setStoreGalleryImages] = useState([]);
    const fileRef = useRef();
    const nav = useNavigate();

    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = typeof asCtx.activeStore === "string" ? JSON.parse(asCtx.activeStore) : asCtx.activeStore;
    const storeLogo = activeStore?.invoiceLogo;
    const vendorIdentifier = (() => {
        try {
            const parsedLogin = typeof authCtx?.loginData === "string" ? JSON.parse(authCtx.loginData) : authCtx?.loginData;
            return parsedLogin?.partnersData?.vendorIdentifier;
        } catch {
            return null;
        }
    })();

    // Compute brand info from activeStore
    const brand = {
        name: activeStore?.storeName || "Aura Salon & Spa",
        initials: (activeStore?.storeName || "AS").split(" ").filter(w => w).map(w => w[0]).slice(0, 2).join("").toUpperCase(),
        area: activeStore?.storeAddress || "Hauz Khas, Delhi",
        phone: activeStore?.storePrimaryPhone || "98100-00000"
    };

    // Merge brand into current offer
    const offer = { ...offerBase, brand };

    const load = useCallback((file) => {
        if (!file?.type.startsWith("image/")) return;
        const r = new FileReader();
        r.onload = e => setImg(e.target.result);
        r.readAsDataURL(file);
    }, []);

    const handleSend = async () => {
        try {
            if (sent) {
                nav("/communications");
                return;
            }

            if (!activeStore?.storeIdentifier || !vendorIdentifier) return;

            setIsSending(true);

            const blob = await downloadPNG({ download: false, updateDLState: false });
            if (!blob) {
                setIsSending(false);
                return;
            }

            const fileName = `${offer.short.replace(/\s+/g, "-").toLowerCase()}-lokaci.png`;
            const cleanedName = cleanFileName(fileName);
            const key = `uploads/${activeStore?.storeIdentifier}/${Date.now()}-${cleanedName}`;

            const { data } = await axios.get(
                "https://bucket-api.lokaci.com/generate-presigned-url",
                {
                    params: {
                        fileName: key,
                        contentType: "image/png",
                        vendorIdentifier,
                        storeIdentifier: activeStore?.storeIdentifier,
                        authIdentifier: authCtx?.authIdentifier,
                        isLogo: false,
                    },
                }
            );

            const { uploadURL } = data;

            await axios.put(uploadURL, blob, {
                headers: { "Content-Type": "image/png" },
            });

            setSent(true);
            setIsSending(false);
        } catch (e) {
            console.error("Send upload error:", e);
            setIsSending(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // CANVAS DOWNLOAD — Production Grade
    //
    // Design: mirror the CSS layout exactly using calculated proportions.
    // SC = 1080 / 380 ≈ 2.84  (canvas px per CSS px in the preview component)
    //
    // Layout has 3 zones matching CSS flexbox justify-content:space-between:
    //   TOP    — logo circle + badge (fixed at PAD from top)
    //   MIDDLE — headline, service, price, note (vertically centered in gap)
    //   BOTTOM — salon name, area, phone badge (fixed at PAD from bottom)
    //
    // Middle zone: measure total content height first, then place at
    //   midTop + (availableSpace - totalContentHeight) / 2
    //   This replicates justify-content:center inside the flex:1 div.
    // ─────────────────────────────────────────────────────────────────────────
    const downloadPNG = async ({ download = true, updateDLState = true } = {}) => {
        if (updateDLState) setDLState("loading");
        try {
            const S = 1080;
            const SC = S / 380;                         // scale factor vs CSS preview
            const PAD = Math.round(22 * SC);             // 62px — matches CSS padding:22

            const canvas = document.createElement("canvas");
            canvas.width = S;
            canvas.height = S;
            const ctx = canvas.getContext("2d");
            const t = tmpl, o = offer;

            // ── Derived sizes (all proportional to CSS values × SC) ──
            const LOGO_D = Math.round(46 * SC);         // logo circle diameter
            const LOGO_R = LOGO_D / 2;
            const HL = Math.round(parseInt(t.hl) * SC * 0.97); // headline px
            const SUB = Math.round(13 * SC);         // service text
            const PR = Math.round(HL * 0.88);       // price text
            const SM = Math.round(10 * SC);         // small (note, area)
            const FOOT_N = Math.round(13 * SC);         // footer salon name
            const PHONE = Math.round(11 * SC);         // phone text

            // ── Explicit font load — ensures canvas can render them ──
            await document.fonts.ready;
            await Promise.allSettled([
                document.fonts.load(`900 ${HL}px "${t.cFont}"`),
                document.fonts.load(`900 ${PR}px "${t.cFont}"`),
                document.fonts.load(`700 ${Math.round(LOGO_R * 0.6)}px "${t.cFont}"`),
                document.fonts.load(`700 ${FOOT_N}px "${t.cFont}"`),
                document.fonts.load(`400 ${SUB}px "${t.cBody}"`),
                document.fonts.load(`700 ${SM}px "${t.cBody}"`),
                document.fonts.load(`600 ${SM}px "${t.cBody}"`),
                document.fonts.load(`700 ${PHONE}px "${t.cBody}"`),
                document.fonts.load(`400 ${PHONE}px "${t.cBody}"`),
            ]);

            // ── LAYER 1: Background image (cover-fit, same as CSS background-size:cover) ──
            if (img) {
                await new Promise((res, rej) => {
                    const bm = new Image();
                    bm.crossOrigin = "anonymous";
                    bm.onload = () => {
                        const sc = Math.max(S / bm.width, S / bm.height);
                        const w = bm.width * sc, h = bm.height * sc;
                        ctx.drawImage(bm, (S - w) / 2, (S - h) / 2, w, h);
                        res();
                    };
                    bm.onerror = rej;
                    bm.src = img;
                });
            } else {
                const fb = ctx.createLinearGradient(0, 0, S, S);
                fb.addColorStop(0, "#1e1b4b");
                fb.addColorStop(1, "#0f172a");
                ctx.fillStyle = fb;
                ctx.fillRect(0, 0, S, S);
            }

            // ── LAYER 2: Brand gradient overlay ──
            const rad = (t.cAngle - 90) * Math.PI / 180;
            const d = S * 0.707;
            const grd = ctx.createLinearGradient(
                S / 2 - Math.cos(rad) * d, S / 2 - Math.sin(rad) * d,
                S / 2 + Math.cos(rad) * d, S / 2 + Math.sin(rad) * d
            );
            grd.addColorStop(0, `rgba(${t.cStop1.join(",")})`);
            grd.addColorStop(1, `rgba(${t.cStop2.join(",")})`);
            ctx.fillStyle = grd;
            ctx.fillRect(0, 0, S, S);

            // ── Helpers ──
            const hex2rgb = h => {
                const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
                return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [255, 255, 255];
            };
            const [ar, ag, ab] = hex2rgb(t.accent);
            const [tr, tg, tb] = hex2rgb(t.text);

            const rrect = (x, y, w, h, r) => {
                ctx.beginPath();
                ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y);
                ctx.quadraticCurveTo(x + w, y, x + w, y + r);
                ctx.lineTo(x + w, y + h - r);
                ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                ctx.lineTo(x + r, y + h);
                ctx.quadraticCurveTo(x, y + h, x, y + h - r);
                ctx.lineTo(x, y + r);
                ctx.quadraticCurveTo(x, y, x + r, y);
                ctx.closePath();
            };

            // Text wrapping utility
            const wrapText = (text, maxWidth, fontSize, fontWeight, fontFamily) => {
                ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}"`;
                const words = text.split(" ");
                const lines = [];
                let currentLine = "";

                for (const word of words) {
                    const testLine = currentLine ? `${currentLine} ${word}` : word;
                    const testWidth = ctx.measureText(testLine).width;
                    if (testWidth > maxWidth && currentLine) {
                        lines.push(currentLine);
                        currentLine = word;
                    } else {
                        currentLine = testLine;
                    }
                }
                if (currentLine) lines.push(currentLine);
                return lines;
            };

            // SVG icon to canvas renderer - enterprise-grade utility
            const renderSvgIcon = async (iconName, size, color, strokeWidth = 2) => {
                const svgMap = {
                    phone: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
                    clock: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`
                };
                
                const svgString = svgMap[iconName];
                if (!svgString) return null;

                return new Promise((resolve, reject) => {
                    const img = new Image();
                    const blob = new Blob([svgString], { type: 'image/svg+xml' });
                    const url = URL.createObjectURL(blob);
                    
                    img.onload = () => {
                        URL.revokeObjectURL(url);
                        resolve(img);
                    };
                    img.onerror = () => {
                        URL.revokeObjectURL(url);
                        reject(new Error(`Failed to load ${iconName} icon`));
                    };
                    img.src = url;
                });
            };

            // ════════════════════════════════════════════════════════════════
            // TOP ROW
            // ════════════════════════════════════════════════════════════════
            const logoX = PAD + LOGO_R;
            const logoY = PAD + LOGO_R;

            // Logo circle
            ctx.beginPath(); ctx.arc(logoX, logoY, LOGO_R, 0, Math.PI * 2);
            ctx.fillStyle = storeLogo ? "#fff" : `rgba(${ar},${ag},${ab},0.12)`;
            ctx.fill();
            ctx.strokeStyle = t.accent;
            ctx.lineWidth = Math.max(2, SC * 1.8);
            ctx.stroke();
            if (storeLogo) {
                try {
                    await new Promise((res, rej) => {
                        const logoImg = new Image();
                        logoImg.crossOrigin = "anonymous";
                        logoImg.onload = () => {
                            ctx.save();
                            ctx.beginPath();
                            ctx.arc(logoX, logoY, LOGO_R - Math.max(2, SC * 1.8), 0, Math.PI * 2);
                            ctx.clip();
                            ctx.drawImage(logoImg, logoX - LOGO_R, logoY - LOGO_R, LOGO_D, LOGO_D);
                            ctx.restore();
                            res();
                        };
                        logoImg.onerror = rej;
                        logoImg.src = storeLogo;
                    });
                } catch {
                    ctx.font = `800 ${Math.round(LOGO_R * 0.62)}px "${t.cFont}"`;
                    ctx.fillStyle = t.accent;
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText(brand.initials, logoX, logoY);
                }
            } else {
                ctx.font = `800 ${Math.round(LOGO_R * 0.62)}px "${t.cFont}"`;
                ctx.fillStyle = t.accent;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(brand.initials, logoX, logoY);
            }

            // Badge (top-right)
            const bFont = Math.round(18 * SC);
            ctx.font = `700 ${bFont}px "${t.cBody}"`;
            ctx.textAlign = "left";
            const bPadH = Math.round(10 * SC);
            const bPadV = Math.round(8 * SC);
            const bTextW = ctx.measureText(o.tag).width;
            const bW = bTextW + bPadH * 2;
            const bH = bFont + bPadV * 2;
            const bR = t.badge === "pill" ? bH / 2 : Math.round(5 * SC);
            const bX = S - PAD - bW;
            const bY = PAD + (LOGO_D - bH) / 2; // vertically align with logo center
            rrect(bX, bY, bW, bH, bR);
            if (t.badge === "fill") {
                ctx.fillStyle = t.accent; ctx.fill();
                ctx.fillStyle = "#000";
            } else {
                ctx.fillStyle = `rgba(${ar},${ag},${ab},0.15)`; ctx.fill();
                ctx.strokeStyle = t.accent;
                ctx.lineWidth = Math.max(1.5, SC * 0.8);
                ctx.stroke();
                ctx.fillStyle = t.accent;
            }
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(o.tag, bX + bW / 2, bY + bH / 2);

            // ════════════════════════════════════════════════════════════════
            // BOTTOM ROW — calculate upward from S - PAD
            // ════════════════════════════════════════════════════════════════
            const footGap = Math.round(1 * SC);      // gap between name and area
            const phonePadH = Math.round(10 * SC);
            const phonePadV = Math.round(5 * SC);

            ctx.font = `700 ${PHONE}px "${t.cBody}"`;
            const phoneText = brand.phone;
            const phoneIconSize = Math.round(11 * SC);
            const phoneIconGap = Math.round(4 * SC);
            const phoneTextW = ctx.measureText(phoneText).width;
            const phoneW = phoneIconSize + phoneIconGap + phoneTextW + phonePadH * 2;
            const phoneH = PHONE + phonePadV * 2;

            // Wrap address text to fit within available width
            const areaMaxWidth = S - PAD * 2 - phoneW - Math.round(10 * SC); // leave gap for phone badge
            const areaLines = wrapText(brand.area, areaMaxWidth, SM, 400, t.cBody);
            const areaLineHeight = Math.round(SM * 1.4); // line height
            const areaTotalHeight = areaLines.length * areaLineHeight;

            // Footer name + area total height
            const footTotalH = FOOT_N + footGap + areaTotalHeight;
            const footerBaseY = S - PAD;                 // bottom edge of content

            // Name starts here (textBaseline top)
            const footNameY = footerBaseY - footTotalH;
            const footAreaY = footNameY + FOOT_N + footGap;
            const phoneBadgeY = footerBaseY - Math.max(footTotalH, phoneH);

            // Draw footer name
            ctx.font = `700 ${FOOT_N}px "${t.cFont}"`;
            ctx.fillStyle = t.text;
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
            ctx.fillText(brand.name, PAD, footNameY);

            // Draw footer area (multi-line)
            ctx.font = `400 ${SM}px "${t.cBody}"`;
            ctx.fillStyle = `rgba(${tr},${tg},${tb},0.45)`;
            areaLines.forEach((line, idx) => {
                ctx.fillText(line, PAD, footAreaY + idx * areaLineHeight);
            });

            // Phone badge
            const phoneBadgeX = S - PAD - phoneW;
            rrect(phoneBadgeX, phoneBadgeY, phoneW, phoneH, Math.round(7 * SC));
            ctx.fillStyle = "rgba(255,255,255,0.10)"; ctx.fill();
            ctx.strokeStyle = "rgba(255,255,255,0.13)";
            ctx.lineWidth = 1;
            ctx.stroke();
            
            // Render phone icon
            // const phoneIconSize = Math.round(11 * SC);
            // const phoneIconGap = Math.round(4 * SC);
            try {
                const phoneIcon = await renderSvgIcon('phone', phoneIconSize, t.text, 2.5);
                const iconX = phoneBadgeX + phonePadH;
                const iconY = phoneBadgeY + (phoneH - phoneIconSize) / 2;
                ctx.drawImage(phoneIcon, iconX, iconY, phoneIconSize, phoneIconSize);
                
                // Phone number text
                ctx.font = `700 ${PHONE}px "${t.cBody}"`;
                ctx.fillStyle = t.text;
                ctx.textAlign = "left";
                ctx.textBaseline = "middle";
                ctx.fillText(phoneText, iconX + phoneIconSize + phoneIconGap, phoneBadgeY + phoneH / 2);
            } catch {
                // Fallback: render text only if icon fails
                ctx.font = `700 ${PHONE}px "${t.cBody}"`;
                ctx.fillStyle = t.text;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(phoneText, phoneBadgeX + phoneW / 2, phoneBadgeY + phoneH / 2);
            }

            // ════════════════════════════════════════════════════════════════
            // MIDDLE — vertically centered between top and bottom zones
            // Mirrors CSS: flex:1 + justifyContent:center
            // Measure all content heights first, then place at center of gap
            // ════════════════════════════════════════════════════════════════
            const midPad = Math.round(12 * SC);       // CSS: padding:"12px 0"
            const midTopY = PAD + LOGO_D + midPad;     // where middle zone starts
            const midBotY = footNameY - midPad;       // where middle zone ends
            const midAvail = midBotY - midTopY;

            // Content block heights
            const hlLineH = Math.round(HL * 0.98);     // lineHeight:.98
            const mbHl = Math.round(8 * SC);       // marginBottom:8 after headline
            const mbSub = Math.round(12 * SC);       // marginBottom:12 after service
            const mtNote = Math.round(10 * SC);       // marginTop:10 before note

            const totalH = hlLineH + hlLineH + mbHl + SUB + mbSub + PR + mtNote + SM;
            const midStartY = midTopY + Math.max(0, (midAvail - totalH) / 2);

            // Headline line 1
            ctx.font = `900 ${HL}px "${t.cFont}"`;
            ctx.fillStyle = t.text;
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
            ctx.fillText(o.headline[0], PAD, midStartY);

            // Headline line 2
            ctx.fillText(o.headline[1], PAD, midStartY + hlLineH);

            // Service
            const subY = midStartY + hlLineH + hlLineH + mbHl;
            ctx.font = `400 ${SUB}px "${t.cBody}"`;
            ctx.fillStyle = `rgba(${tr},${tg},${tb},0.80)`;
            ctx.fillText(o.sub, PAD, subY);

            // Price row — was (strikethrough) + now
            const priceY = subY + SUB + mbSub;
            let priceX = PAD;

            if (o.was) {
                ctx.font = `400 ${SUB}px "${t.cBody}"`;
                ctx.fillStyle = `rgba(${tr},${tg},${tb},0.38)`;
                // Vertically align was-text to baseline-center of price
                const wasOffsetY = priceY + (PR - SUB) / 2;
                const wasW = ctx.measureText(o.was).width;
                ctx.textBaseline = "top";
                ctx.fillText(o.was, priceX, wasOffsetY);
                // Strikethrough at mid-height of was-text
                ctx.strokeStyle = `rgba(${tr},${tg},${tb},0.38)`;
                ctx.lineWidth = Math.max(1.5, SC * 0.8);
                ctx.beginPath();
                ctx.moveTo(priceX, wasOffsetY + SUB * 0.52);
                ctx.lineTo(priceX + wasW, wasOffsetY + SUB * 0.52);
                ctx.stroke();
                priceX += wasW + Math.round(12 * SC);
            }

            // Price "now"
            ctx.font = `900 ${PR}px "${t.cFont}"`;
            ctx.fillStyle = t.accent;
            ctx.textBaseline = "top";
            ctx.fillText(o.now, priceX, priceY);

            // Note / validity with clock icon
            const noteY = priceY + PR + mtNote;
            const clockIconSize = Math.round(10 * SC);
            const clockIconGap = Math.round(4 * SC);
            
            try {
                const clockIcon = await renderSvgIcon('clock', clockIconSize, t.accent, 2.5);
                ctx.globalAlpha = 0.82;
                ctx.drawImage(clockIcon, PAD, noteY + (SM - clockIconSize) / 2, clockIconSize, clockIconSize);
                
                ctx.font = `600 ${SM}px "${t.cBody}"`;
                ctx.fillStyle = t.accent;
                ctx.textAlign = "left";
                ctx.textBaseline = "top";
                ctx.fillText(o.note, PAD + clockIconSize + clockIconGap, noteY);
                ctx.globalAlpha = 1;
            } catch {
                // Fallback: render text only
                ctx.font = `600 ${SM}px "${t.cBody}"`;
                ctx.fillStyle = t.accent;
                ctx.globalAlpha = 0.82;
                ctx.textAlign = "left";
                ctx.textBaseline = "top";
                ctx.fillText(o.note, PAD, noteY);
                ctx.globalAlpha = 1;
            }

            // ── Export ──
            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((b) => {
                    if (b) resolve(b);
                    else reject(new Error("Failed to create PNG blob"));
                }, "image/png");
            });

            if (download) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${o.short.replace(/\s+/g, "-").toLowerCase()}-lokaci.png`;
                a.click();
                URL.revokeObjectURL(url);
            }

            if (updateDLState) {
                setDLState("done");
                setTimeout(() => setDLState("idle"), 2200);
            }

            return blob;

        } catch (e) {
            console.error("Canvas error:", e);
            if (updateDLState) setDLState("idle");
            return null;
        }
    };

    return (
        <div style={{ fontFamily: "'DM Sans',sans-serif", background: "#F4F5F8", height: "100svh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Bebas+Neue&family=Abril+Fatface&family=Cormorant+Garamond:wght@600;700&family=Cinzel:wght@600;700&family=Yeseva+One&family=DM+Sans:wght@300;400;500;600;700&family=Nunito:wght@700&family=Jost:wght@400;500&family=Hind:wght@600&family=Lato:wght@400;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        :root{
          --bg:#F4F5F8; --surface:#FFFFFF; --border:#E8EAEF;
          --text:#1C1F2E; --sub:#8B90A0;
          --accent:#4F46E5; --accent-soft:rgba(79,70,229,.08); --accent-mid:rgba(79,70,229,.18);
          --green:#059669; --radius:10px;
        }
        .tmpl-cell{cursor:pointer;border-radius:8px;overflow:hidden;border:2px solid transparent;transition:all .15s}
        .tmpl-cell:hover{border-color:rgba(79,70,229,.3)}
        .tmpl-cell.sel{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-mid)}
        .offer-row{display:flex;align-items:center;gap:10px;padding:9px 11px;border-radius:9px;cursor:pointer;border:1.5px solid var(--border);background:var(--surface);transition:all .14s}
        .offer-row:hover{border-color:rgba(79,70,229,.25);background:var(--accent-soft)}
        .offer-row.sel{border-color:var(--accent);background:var(--accent-soft)}
        .upload-box{border:2px dashed var(--border);border-radius:var(--radius);cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;transition:all .15s;background:var(--surface)}
        .upload-box.drag{border-color:var(--accent);background:var(--accent-soft)}
        .upload-box:hover{border-color:rgba(79,70,229,.3)}
        .btn-send{width:100%;padding:0;border:none;border-radius:var(--radius);background:var(--accent);color:#fff;font-size:14px;font-weight:600;cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .15s;display:flex;align-items:center;justify-content:center;gap:7px}
        .btn-send:hover{background:#4338CA}
        .btn-send.done{background:var(--green)}
        .label{font-size:10px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:var(--sub);margin-bottom:7px}
        .step-num{width:18px;height:18px;border-radius:50%;background:var(--accent);color:#fff;font-size:9px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0}
        .pill{padding:2px 8px;border-radius:20px;font-size:11px;font-weight:600}
        .format-chip{padding:3px 10px;border-radius:20px;font-size:10px;font-weight:600;cursor:pointer;border:1.5px solid var(--border);color:var(--sub);background:var(--surface);transition:all .13s;white-space:nowrap}
        .format-chip.sel{border-color:var(--accent);color:var(--accent);background:var(--accent-soft)}
        .scrollbar-hide::-webkit-scrollbar{display:none}
        .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
        .btn-dl{width:100%;padding:9px;border-radius:9px;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:5px;transition:all .15s;border:1px solid var(--border);background:var(--bg);color:var(--sub)}
        .btn-dl:hover:not(:disabled){background:#e8eaed;color:var(--text)}
        .btn-dl:disabled{cursor:wait;border-color:var(--accent);color:var(--accent);background:var(--accent-soft)}
        .btn-dl.done{border-color:var(--green);color:var(--green);background:rgba(5,150,105,.06)}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

            {/* ── HEADER ── */}
            <div style={{ height: 48, background: "#fff", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg,#4F46E5,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 12L5.5 4L7 8L9 5L12 12" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Lokaci Pro</span>
                    <span style={{ width: 1, height: 14, background: "var(--border)", display: "block" }} />
                    <span style={{ fontSize: 13, color: "var(--sub)", fontWeight: 400 }}>Creative Studio</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="pill" style={{ background: "rgba(5,150,105,.1)", color: "var(--green)", border: "1px solid rgba(5,150,105,.2)" }}>✓ WhatsApp Connected</span>
                    <span className="pill" style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent-mid)" }}>5 / 10 creatives</span>
                </div>
            </div>

            {/* ── BODY — 3 columns ── */}
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "300px 1fr 310px", overflow: "hidden", gap: 0 }}>

                {/* ══ COL 1 ══ */}
                <div style={{ background: "#fff", borderRight: "1px solid var(--border)", padding: "14px 14px", display: "flex", flexDirection: "column", gap: 14, overflow: "hidden" }}>
                    <div style={{ flexShrink: 0 }}>
                        <div className="label" style={{ display: "flex", alignItems: "center", gap: 5 }}><span className="step-num">1</span> Photo</div>
                        {img ? (
                            <div style={{ position: "relative", height: 68, borderRadius: 9, overflow: "hidden" }}>
                                <img src={img} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <button onClick={() => setImg(null)} style={{ padding: "3px 10px", borderRadius: 6, background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Badlo</button>
                                </div>
                            </div>
                        ) : (
                            <div className={`upload-box${drag ? " drag" : ""}`} style={{ height: 68 }}
                                onDragOver={e => { e.preventDefault(); setDrag(true); }}
                                onDragLeave={() => setDrag(false)}
                                onDrop={e => { e.preventDefault(); setDrag(false); load(e.dataTransfer.files[0]); }}
                                onClick={() => fileRef.current.click()}
                            >
                                <ImageIcon size={18} strokeWidth={2} style={{ opacity: 0.4 }} />
                                <div style={{ fontSize: 11, color: "var(--sub)", fontWeight: 500 }}>Select Background Image</div>
                                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => load(e.target.files[0])} />
                            </div>
                        )}

                        <button 
                            onClick={() => setGalleryOpen(true)}
                            style={{ 
                                width: "100%", 
                                padding: "8px 12px", 
                                borderRadius: 8, 
                                border: "1px solid var(--border)", 
                                background: "var(--surface)", 
                                color: "var(--sub)", 
                                fontSize: 12, 
                                fontWeight: 600, 
                                cursor: "pointer", 
                                fontFamily: "'DM Sans',sans-serif",
                                transition: "all 0.15s",
                                marginTop: 8
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = "var(--bg)";
                                e.currentTarget.style.borderColor = "var(--accent)";
                                e.currentTarget.style.color = "var(--accent)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = "var(--surface)";
                                e.currentTarget.style.borderColor = "var(--border)";
                                e.currentTarget.style.color = "var(--sub)";
                            }}
                        >
                            Choose from gallery
                        </button>

                    </div>

                    <div style={{ flexShrink: 0 }}>
                        <div className="label" style={{ display: "flex", alignItems: "center", gap: 5 }}><span className="step-num">2</span> Style Chuno</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 5 }}>
                            {T.map(t => (
                                <div key={t.id} className={`tmpl-cell${tmpl.id === t.id ? " sel" : ""}`} onClick={() => setTmpl(t)}>
                                    <MiniCreative t={t} o={offer} img={img} logo={storeLogo} />
                                    <div style={{ padding: "3px 4px", background: tmpl.id === t.id ? "var(--accent-soft)" : "#fafafa", fontSize: 9, fontWeight: 600, color: tmpl.id === t.id ? "var(--accent)" : "var(--sub)", textAlign: "center" }}>{t.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ flexShrink: 0 }}>
                        <div className="label">Format</div>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {["1:1 Post", "Story", "WhatsApp"].map((f, i) => (
                                <div key={f} className={`format-chip${i === 0 ? " sel" : ""}`}>{f}</div>
                            ))}
                        </div>
                    </div>

                    <div style={{ flex: 1 }} />

                    <div style={{ flexShrink: 0, background: "var(--bg)", borderRadius: 9, padding: "10px 11px", border: "1px solid var(--border)" }}>
                        <div className="label">AI Caption</div>
                        <div style={{ fontSize: 11, color: "var(--sub)", lineHeight: 1.65 }}>
                            🎉 <strong style={{ color: "var(--text)" }}>{offerBase.short}</strong> at {brand.name}!<br />
                            {offerBase.sub} — sirf <strong style={{ color: "var(--accent)" }}>{offerBase.now}</strong> mein ✨<br />
                            <span style={{ opacity: .7 }}>⏰ {offerBase.note}</span>
                        </div>
                        <div style={{ marginTop: 7, fontSize: 10, color: "var(--accent)", cursor: "pointer", fontWeight: 600 }}>🔄 Dobara likho →</div>
                    </div>
                </div>

                {/* ══ COL 2 — Preview ══ */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "18px 20px", background: "var(--bg)", gap: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--sub)", alignSelf: "flex-start" }}>Live Preview</div>
                    <div style={{ width: "100%", maxWidth: 380, maxHeight: "calc(100svh - 48px - 80px)", aspectRatio: "1/1" }}>
                        <Creative t={tmpl} o={offer} img={img} logo={storeLogo} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)" }} />
                        <span style={{ fontSize: 11, color: "var(--sub)" }}>1080×1080 · PNG · WhatsApp Ready</span>
                    </div>
                </div>

                {/* ══ COL 3 — Offers + Send ══ */}
                <div style={{ background: "#fff", borderLeft: "1px solid var(--border)", padding: "14px 14px", display: "flex", flexDirection: "column", gap: 14, overflow: "hidden" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minHeight: 0 }}>
                        <div className="label" style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}><span className="step-num">3</span> Offer Chuno</div>
                        <div className="scrollbar-hide" style={{ display: "flex", flexDirection: "column", gap: 6, overflowY: "auto", flex: 1 }}>
                            {OFFERS.map(o => (
                                <div key={o.id} className={`offer-row${offerBase.id === o.id ? " sel" : ""}`} onClick={() => setOfferBase(o)}>
                                    <div style={{ fontSize: 18, flexShrink: 0 }}>{o.emoji}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: offerBase.id === o.id ? "var(--accent)" : "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.short}</div>
                                        <div style={{ fontSize: 10, color: "var(--sub)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.sub}</div>
                                    </div>
                                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: offerBase.id === o.id ? "var(--accent)" : "var(--text)" }}>{o.now}</div>
                                        {o.was && <div style={{ fontSize: 9, color: "var(--sub)", textDecoration: "line-through" }}>{o.was}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ height: 1, background: "var(--border)", flexShrink: 0 }} />

                    <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div className="label" style={{ display: "flex", alignItems: "center", gap: 5 }}><span className="step-num">4</span> Bhejo</div>

                        <div style={{ background: "var(--bg)", borderRadius: 9, padding: "10px 12px", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)" }}>847 Customers</div>
                                <div style={{ fontSize: 10, color: "var(--sub)", marginTop: 1 }}>WhatsApp list ready</div>
                            </div>
                            <div style={{ fontSize: 18 }}>💬</div>
                        </div>

                        <button className={`btn-send${sent ? " done" : ""}`} style={{ height: 44 }} onClick={handleSend} disabled={isSending}>
                            {isSending
                                ? <><span>⏳</span><span>Uploading...</span></>
                                : sent
                                    ? <><span>✓</span><span>Go To Campaign</span></>
                                    : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg><span>Use Image</span></>
                            }
                        </button>

                        <button
                            className={`btn-dl${dlState === "done" ? " done" : ""}`}
                            onClick={downloadPNG}
                            disabled={dlState === "loading"}
                        >
                            {dlState === "loading" && <span style={{ display: "inline-block", animation: "spin .9s linear infinite" }}>⏳</span>}
                            {dlState === "done" && "✓"}
                            {dlState === "idle" && "⬇"}
                            {" "}
                            {dlState === "loading" ? "Bana raha hai…" : dlState === "done" ? "Download ho gaya!" : "PNG Download Karo"}
                        </button>
                    </div>
                </div>
            </div>
            <CreativeStudioGallery 
                isOpen={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                onSelectImage={setImg}
                storeImages={storeGalleryImages}
                vendorIdentifier={vendorIdentifier}
                storeIdentifier={activeStore?.storeIdentifier}
                authIdentifier={authCtx?.authIdentifier}
                onStoreImagesUploaded={(newItems) => {
                    setStoreGalleryImages((prev) => [...newItems, ...prev]);
                }}
            />
        </div>
    );
}